/**
 * The two bombing raids: flying one, and stopping one.
 *
 * They are the same mission from opposite ends, which is why they are the same
 * file and why almost everything in them comes out of `raid-parts.ts`. The
 * manual's own one-liners:
 *
 * - *"**Escort a Bombing Raid** — Your bombers are about to penetrate deep into
 *   enemy territory to bomb strategic targets like RR yards, factories, and
 *   supply dumps. It's your mission to see that they succeed."*
 * - *"**Stop a Bombing Raid** — Intercept enemy bombers who are trying to bomb
 *   your side."*
 *
 * Both are decided on `raidOutcome` (`objectives.ts`) rather than on a body
 * count, because a raid that dropped everything and missed is a failed escort
 * even with every bomber alive, and a raid that got its load away is a failed
 * interception even with the whole formation shot down on the way home.
 *
 * **Both now carry trains (user decision, 2026-08-23).** A raid's target site
 * — the rail yard, the factory, the supply dump — is exactly the kind of
 * rear-area ground the patrol's trains already run behind: real ground
 * targets, already on the map. Trains are drawn the same way the patrol draws
 * them (`ground-traffic.ts`'s recovered class-5 mechanics) but from a *separate*
 * salted stream (`raidTraffic`, below) so adding them cannot shift which
 * aeroplane or which weather a generator draws next — `flak-placement.ts`'s own
 * convention for exactly this reason. A train takes the raid site's own side:
 * hostile ground behind the enemy's lines on the escort mission, friendly
 * ground behind the player's own on the interception, the same way the raid
 * structures themselves do.
 */

import { assembleMission } from "./mission-assemble.ts";
import { MissionDraw, waypoint } from "./mission-build.ts";
import { missionContext, type MissionOptions } from "./mission-options.ts";
import { scoutsFor } from "./mission-roster.ts";
import { trainTraffic } from "./ground-traffic.ts";
import type { MissionFlight, MissionSpec, MissionTarget } from "./mission-spec.ts";
import type { MissionObjective } from "./objectives.ts";
import type { RaidPlan } from "./raid.ts";
import {
  BOMB_RUN_ALTITUDE_M,
  ESCORT_STACK_M,
  INTERCEPT_STACK_M,
  RAID_NOTES,
  TARGET_IDS,
  bomberFlights,
  bomberIdsFor,
  bomberNameOf,
  bomberTypeFor,
  hostileScreen,
  playerBombLoad,
  playerSection,
  raidBriefingLines,
  raidTargets,
  PLAYER_BOMB_NOTE,
  type StrikeSite,
} from "./raid-parts.ts";

/**
 * How much further behind the raid's own target site a train's line runs.
 * `StrikeSite.line` sits only 1.7-2.9 km from the front (`generateEscortBombers`,
 * `generateStopBombers`), and the patrol's own 24-40 km route length would run
 * a route's whole length along `z` before the bearing draw ever turned it — far
 * enough to put a train the other side of the front from the raid it belongs
 * to. So a raid's trains get their own, shorter route (below) *and* a setback
 * pushing their line deeper into whichever side's ground the site already
 * stands on, so no draw of bearing and length can walk one across the wire —
 * every generator's own test pins targets to their own side of it.
 */
const RAID_TRAFFIC_SETBACK_M = 1500;
/** Half-width of the band a raid's trains run, tighter than the patrol's for the same reason. */
const RAID_TRAFFIC_SPREAD_M = 500;
/**
 * A raid's trains run a short line rather than the patrol's 24-40 km: the
 * working area here is a target site a couple of kilometres from the front, not
 * the patrol's wide-open sweep, and `RAID_TRAFFIC_SETBACK_M` only buys so much
 * clearance.
 */
const RAID_TRAFFIC_MIN_LENGTH_M = 2000;
const RAID_TRAFFIC_MAX_LENGTH_M = 4000;

/**
 * One or two trains on the line behind a raid's own target site, drawn from a
 * stream salted `${catalogId}:traffic` and seeded off the mission — never the
 * generator's own `draw` — so adding them cannot shift a single weather roll,
 * aeroplane pick or site coordinate anywhere else in the mission
 * (`flak-placement.ts`'s convention, `frontLineFlak`'s `:flak` salt). Side
 * matches whichever ground the raid's own structures stand on: hostile behind
 * the enemy's lines on the escort mission, friendly behind the player's own on
 * the interception.
 */
function raidTraffic(seed: number, catalogId: string, site: StrikeSite, side: "friendly" | "hostile"): readonly MissionTarget[] {
  const draw = new MissionDraw(seed, `${catalogId}:traffic`);
  // The site's own `line` already carries the sign of whichever side it is on
  // (negative into hostile ground, positive into friendly), so pushing further
  // the same direction is just extending that sign.
  const lineZ = site.line + Math.sign(site.line) * RAID_TRAFFIC_SETBACK_M;
  return trainTraffic(draw, {
    lineZ,
    spreadM: RAID_TRAFFIC_SPREAD_M,
    side,
    minLengthM: RAID_TRAFFIC_MIN_LENGTH_M,
    maxLengthM: RAID_TRAFFIC_MAX_LENGTH_M,
  });
}

/**
 * **ESCORT A BOMBING RAID.** A vic of heavies crosses the lines to level a rail
 * yard, a factory and a supply dump; the player flies above them and keeps the
 * Jagdstaffel off. Won by the raid working, not by the bombers merely surviving
 * — which is the whole reason this type could not be flown before.
 */
export function generateEscortBombers(options: MissionOptions): MissionSpec {
  const context = missionContext(options, "escort-bombers");
  const { draw } = context;
  // One heavy per target, so the raid can level what it was sent for: a bomber
  // empties most of its rack in the few seconds its solution holds, and two
  // aeroplanes queueing over one shed is not how a raid was flown.
  const bomberCount = TARGET_IDS.length;
  const bomber = bomberTypeFor(context.nation);
  const enemyCount = draw.int(1, 2);
  const enemyAircraft = options.enemyAircraft ?? draw.pick(scoutsFor(context.enemyNation, context.era));
  const site: StrikeSite = { line: -1700 - draw.between(0, 400), spread: draw.between(320, 520) };
  const escortAltitude = BOMB_RUN_ALTITUDE_M + ESCORT_STACK_M;
  const enemyAltitude = escortAltitude + draw.int(1, 3) * 150;
  const targets = raidTargets(site, "hostile");
  const bomberIds = bomberIdsFor(bomberCount);
  // Behind the enemy's own lines, same as the raid structures — the trains a
  // raid escort passes are the enemy's, not the player's own.
  const trains = raidTraffic(context.seed, "escort-bombers", site, "hostile");
  // The one mission in the port whose structures belong to the *other* side, so
  // the one a player-carried rack can be pointed at without an own goal.
  const playerBombs = playerBombLoad(context);

  const flights: MissionFlight[] = [
    ...bomberFlights({
      count: bomberCount,
      aircraft: bomber,
      side: "friendly",
      startZ: 1400,
      homeZ: 2600,
      callsign: "Bomber",
      targets,
    }),
    ...playerSection({
      context,
      position: { x: 200, y: escortAltitude, z: 1700 },
      headingRad: 0,
      wingmen: 2,
      playerBombs,
      ammunition: options.ammunition,
      waypoints: [
        waypoint({ x: 0, y: escortAltitude, z: site.line }, "TARGET"),
        waypoint({ x: 0, y: escortAltitude, z: 2600 }, "HOME"),
      ],
    }),
    ...hostileScreen({
      count: enemyCount,
      aircraft: enemyAircraft,
      callsign: "Enemy",
      anchor: { x: site.spread, y: enemyAltitude, z: site.line - 1400 },
      station: waypoint({ x: 0, y: enemyAltitude, z: site.line + 200 }, "INTERCEPT"),
    }),
  ];

  return assembleMission(context, {
    catalogId: "escort-bombers",
    type: "escortBombers",
    title: "Escort a bombing raid",
    flights,
    targets: [...targets, ...trains],
    objectives: escortObjectives(bomberIds),
    raid: { role: "escort", raiderIds: bomberIds, targetIds: TARGET_IDS } satisfies RaidPlan,
    briefing: [
      ...raidBriefingLines({
        context,
        opening: `${bomberCount} ${bomberNameOf(bomber)} heavies are going for the rail yard, the factory and the supply dump behind the lines, and they will make their run at 1,200 feet.`,
        flightSize: 3,
        enemyCount,
        enemyLeader: "the Staffel over the target",
        enemyAircraft,
        ownAltitudeM: escortAltitude,
        enemyAltitudeM: enemyAltitude,
      }),
      ...(playerBombs
        ? [
            `You are going up with ${playerBombs.count} light bomb${playerBombs.count === 1 ? "" : "s"} under the fuselage. Nobody will aim them for you — get down over the target, lean out and let them go.`,
          ]
        : []),
    ],
    portNotes: playerBombs ? [...RAID_NOTES, PLAYER_BOMB_NOTE] : RAID_NOTES,
  });
}

function escortObjectives(bomberIds: readonly string[]): readonly MissionObjective[] {
  return [
    { kind: "raidOutcome", id: "raid-targets", label: "See the raid onto its targets", role: "primary" },
    {
      kind: "protect",
      id: "protect-bombers",
      label: "Bring at least one bomber home",
      role: "survival",
      flightIds: bomberIds,
      // A raid that loses aeroplanes and still bombs is a raid that worked; only
      // losing the whole formation is a failed escort.
      allowedLosses: bomberIds.length - 1,
    },
    { kind: "returnToBase", id: "rtb", label: "Return across your own lines", role: "primary", flightId: "player" },
  ];
}

/**
 * **STOP A BOMBING RAID.** The mirror image, and the manual's own one-liner:
 * *"Intercept enemy bombers who are trying to bomb your side."* The targets are
 * yours, the raid is inbound, and the mission is decided on whether it gets its
 * load away.
 */
export function generateStopBombers(options: MissionOptions): MissionSpec {
  const context = missionContext(options, "stop-bombers");
  const { draw } = context;
  const bomberCount = draw.int(2, 3);
  const bomber = bomberTypeFor(context.enemyNation);
  const escortCount = draw.int(1, 2);
  const escortAircraft = options.enemyAircraft ?? draw.pick(scoutsFor(context.enemyNation, context.era));
  const site: StrikeSite = { line: 2400 + draw.between(0, 500), spread: draw.between(320, 520) };
  const escortAltitude = BOMB_RUN_ALTITUDE_M + ESCORT_STACK_M;
  const patrolAltitude = BOMB_RUN_ALTITUDE_M + INTERCEPT_STACK_M;
  const targets = raidTargets(site, "friendly");
  const bomberIds = bomberIdsFor(bomberCount);
  // Behind the player's own lines, same as the raid structures being defended
  // — these are friendly trains on friendly ground, not something to shoot.
  const trains = raidTraffic(context.seed, "stop-bombers", site, "friendly");

  const flights: MissionFlight[] = [
    ...bomberFlights({
      count: bomberCount,
      aircraft: bomber,
      side: "hostile",
      startZ: -2200,
      homeZ: -3000,
      callsign: "Raider",
      targets,
    }),
    // Formed up between the raid and what it is coming for, and above it — the
    // manual's warning runs the other way ("don't put the intercepting scouts
    // too high"), so the stack is deliberately modest.
    ...playerSection({
      context,
      position: { x: 0, y: patrolAltitude, z: site.line - 1600 },
      headingRad: 0,
      wingmen: 2,
      ammunition: options.ammunition,
      waypoints: [
        waypoint({ x: 0, y: patrolAltitude, z: -600 }, "INTERCEPT"),
        waypoint({ x: 0, y: patrolAltitude, z: site.line }, "TARGET"),
      ],
    }),
    ...hostileScreen({
      count: escortCount,
      aircraft: escortAircraft,
      callsign: "Escort",
      anchor: { x: 300, y: escortAltitude, z: -2500 },
      station: waypoint({ x: 0, y: escortAltitude, z: site.line }, "COVER"),
    }),
  ];

  return assembleMission(context, {
    catalogId: "stop-bombers",
    type: "stopBombers",
    title: "Stop a bombing raid",
    flights,
    targets: [...targets, ...trains],
    objectives: INTERCEPT_OBJECTIVES,
    raid: { role: "intercept", raiderIds: bomberIds, targetIds: TARGET_IDS } satisfies RaidPlan,
    briefing: raidBriefingLines({
      context,
      opening: `${bomberCount} ${bomberNameOf(bomber)} heavies are inbound for the rail yard behind you, and they will come in at 1,200 feet.`,
      flightSize: 3,
      enemyCount: escortCount,
      enemyLeader: "the raid's escort",
      enemyAircraft: escortAircraft,
      ownAltitudeM: patrolAltitude,
      enemyAltitudeM: escortAltitude,
    }),
    portNotes: [
      ...RAID_NOTES,
      "The bombers' three gun stations are modelled and will shoot back on their real traverse arcs, but the manual's note that the Handley Page mounted *dual* guns on each swivel is not — a station is a station.",
    ],
  });
}

const INTERCEPT_OBJECTIVES: readonly MissionObjective[] = [
  { kind: "raidOutcome", id: "break-raid", label: "Break up the raid before it bombs", role: "primary" },
  {
    kind: "protectTargets",
    id: "protect-targets",
    label: "Keep the raid off the yard, the factory and the dump",
    role: "survival",
    targetIds: TARGET_IDS,
    // The raid pass mark, from the other side: lose half and the raid worked.
    // Redundant with the objective above by design — it is what puts the stakes
    // on the HUD, and it settles the moment they are lost rather than waiting
    // for the last bomber to fly home empty.
    allowedLosses: 1,
  },
];
